import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { formatCandidateShortName } from '../utils/candidatePrivacy';

/**
 * TASKNEIROKLYUCH-765: модалка карточки кандидата Avito.
 *
 * Зачем отдельная (а не общая с EStaffResumeModal): у Avito принципиально
 * другой набор полей и текст резюме приходит plain (одно поле `description`),
 * тогда как E-Staff отдаёт большой HTML-документ. Чтобы не плодить условные
 * ветки внутри EStaffResumeModal и не превращать общий маппер в свалку
 * костылей под чужой UI, рендерим Avito-данные напрямую из hh-подобной
 * структуры, которую отдаёт mapAvitoToHhLikeRow.
 *
 * Источник данных: готовый resumeData из Avito search/applications.
 *
 * Принцип отображения: если поле пустое (null/undefined/[]) — соответствующая
 * секция или строка просто не рендерится. Без прочерков и плейсхолдеров.
 */

interface AvitoResumeModalProps {
  open: boolean;
  onClose: () => void;
  resumeData: any;
  searchKeywords?: string[];
  searchCities?: string[];
}

// Готовности у Avito по спеке приходят русскими строками-енумами
// ('Возможен'/'Невозможен'/'Готов'/'Не готов'/'Иногда') — показываем их как есть,
// ничего не нормализуем.
const formatReadiness = (value: any): string | null => {
  if (value == null) return null;
  if (typeof value === 'string' && value.trim()) return value;
  return null;
};

// Локализация enum-кодов с root-уровня Resume2.0 (см. avitoMappers.js).
const EDUCATION_LEVEL_LABELS: Record<string, string> = {
  'higher': 'Высшее',
  'unfinished-higher': 'Неоконченное высшее',
  'secondary': 'Среднее',
  'special-secondary': 'Среднее специальное',
};
const DRIVING_EXPERIENCE_LABELS: Record<string, string> = {
  'less-than-three-years': 'Меньше трёх лет',
  'more-than-three-years': 'Больше трёх лет',
};
const OWN_TRANSPORT_LABELS: Record<string, string> = {
  'false': 'Нет',
  'no': 'Нет',
  'car': 'Легковое авто',
  'cargo-car': 'Грузовое авто',
  'bike': 'Мотоцикл',
  'scooter': 'Мопед',
};

const normalizeText = (value: any): string => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).replace(/\s+/g, ' ').trim();
};

const isAvitoResumeIdLabel = (value: any): boolean => /^Резюме\s*№/i.test(normalizeText(value));

const getAvitoCandidateName = (resume: any): string | null => {
  if (!resume || typeof resume !== 'object') return null;
  const title = normalizeText(resume.title || resume.position);
  const rawName = normalizeText(resume.fullName || resume.full_name || resume.name || resume.displayName);
  const hasNameParts = Boolean(
    normalizeText(resume.firstName || resume.first_name) ||
    normalizeText(resume.lastName || resume.last_name) ||
    normalizeText(resume.middleName || resume.middle_name)
  );
  const hasRealFullName = Boolean(
    rawName &&
    !isAvitoResumeIdLabel(rawName) &&
    (!title || rawName !== title)
  );
  if (!hasNameParts && !hasRealFullName) return null;

  return formatCandidateShortName({
    firstName: resume.firstName,
    first_name: resume.first_name,
    lastName: resume.lastName,
    last_name: resume.last_name,
    middleName: resume.middleName,
    middle_name: resume.middle_name,
    fullName: resume.fullName || resume.full_name,
    name: resume.name,
    displayName: resume.displayName,
  }, '');
};

const AvitoResumeModal: React.FC<AvitoResumeModalProps> = ({
  open,
  onClose,
  resumeData,
  searchKeywords = [],
  searchCities = [],
}) => {
  const colors = useBrandingColors();
  const [resume, setResume] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Открыта ли увеличенная аватарка (клик по фото в шапке)
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  // Подсветка совпадений ключевых слов в карточке
  const highlightText = (text: any): React.ReactNode => {
    const textStr = typeof text === 'string'
      ? text
      : (text == null ? '' : String(text));
    if (!textStr || !Array.isArray(searchKeywords) || searchKeywords.length === 0) {
      return textStr;
    }
    const valid = searchKeywords.filter(Boolean);
    if (valid.length === 0) return textStr;
    const escaped = valid.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'giu');
    const parts = textStr.split(regex);
    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part)
            ? <mark key={i} className="bg-yellow-300">{part}</mark>
            : <React.Fragment key={i}>{part}</React.Fragment>
        )}
      </span>
    );
  };

  const matchedKeywords = useMemo(() => {
    if (!resume) return [];
    const text = JSON.stringify(resume);
    const found: string[] = [];
    const seen = new Set<string>();
    for (const kw of searchKeywords) {
      const s = String(kw ?? '').trim();
      if (!s || seen.has(s.toLowerCase())) continue;
      seen.add(s.toLowerCase());
      const escaped = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(^|[^а-яёА-ЯЁa-zA-Z0-9_])(${escaped})`, 'iu');
      if (regex.test(text)) found.push(s);
    }
    return found;
  }, [resume, searchKeywords]);

  useEffect(() => {
    if (!open) {
      // При закрытии модалки сбрасываем зум — иначе при повторном открытии
      // мог бы остаться overlay поверх нового резюме
      setIsImageZoomed(false);
      setResume(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setError(null);
    setIsLoading(false);
    setResume(resumeData || null);
  }, [open, resumeData]);

  const isCityMatched = (cityName?: string | null) => {
    if (!cityName || !searchCities || searchCities.length === 0) return false;
    return searchCities.some((c) =>
      cityName.toLowerCase().includes(String(c).toLowerCase()) ||
      String(c).toLowerCase().includes(cityName.toLowerCase())
    );
  };

  // Avito отдаёт total_experience в годах, маппер переводит в месяцы — здесь
  // снова приводим к "X лет Y мес" для человеческого отображения
  const formatExperience = (months?: number | null) => {
    if (!months || months <= 0) return null;
    const years = Math.floor(months / 12);
    const rest = months % 12;
    const parts: string[] = [];
    if (years > 0) parts.push(`${years} лет`);
    if (rest > 0) parts.push(`${rest} мес`);
    return parts.join(' ');
  };

  const formatDate = (s?: string | null) => {
    if (!s) return '';
    try {
      return new Date(s).toLocaleDateString('ru-RU', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch {
      return s;
    }
  };

  if (!open) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl p-6 flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.primary }}></div>
          <p className="mt-4 text-center">Загрузка резюме...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl p-6 max-w-md">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ошибка загрузки</h3>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={onClose}
            className="mt-6 w-full px-4 py-2 text-white rounded-3xl font-medium transition-colors"
            style={{ backgroundColor: colors.primary }}
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  // Безопасно разбираем поля, которые приходят из mapAvitoToHhLikeRow.
  // Маппер держит много алиасов (photo / photoUrl, experience / experiences,
  // education.primary / educations, skill_set / skills), чтобы строки таблицы
  // и карточка читали одно и то же — здесь берём удобный вариант.
  const photoUrl: string | null =
    resume?.photoUrl || resume?.photo?.medium || resume?.photo?.url || null;
  const position: string = resume?.position || resume?.title || '';
  const idLabel: string = resume?.externalId
    ? `Резюме №${resume.externalId}`
    : resume?.id ? `Резюме №${resume.id}` : 'Резюме';
  const candidateName: string | null = getAvitoCandidateName(resume);
  const cityName: string | null = resume?.area?.name || resume?.city || null;
  const fullAddress: string | null = resume?.address || null;
  const metro: string | null = resume?.metro || null;
  const district: string | null = resume?.district || null;
  const coordinates: { latitude: number; longitude: number } | null = resume?.coordinates || null;
  const salaryAmount: number | null = resume?.salaryAmount ?? resume?.salary?.amount ?? null;
  const totalMonths: number | null = resume?.totalExperienceMonths
    ?? resume?.total_experience?.months ?? null;
  const formattedExperience = formatExperience(totalMonths);
  const description: string = resume?.description || '';
  const experiences: any[] = Array.isArray(resume?.experience)
    ? resume.experience
    : (Array.isArray(resume?.experiences) ? resume.experiences : []);
  const educations: any[] = Array.isArray(resume?.educations)
    ? resume.educations
    : (Array.isArray(resume?.education?.primary) ? resume.education.primary : []);
  const skills: string[] = Array.isArray(resume?.skills)
    ? resume.skills
    : (Array.isArray(resume?.skill_set)
        ? resume.skill_set.map((s: any) => (typeof s === 'string' ? s : s?.name)).filter(Boolean)
        : []);
  const languages: { name: string; level?: string | null }[] = Array.isArray(resume?.languages)
    ? resume.languages.filter((l: any) => l?.name)
    : [];
  const gender: string | null = resume?.gender?.id || resume?.gender || null;
  const citizenship: string | null = resume?.citizenship?.name || null;
  const alternateUrl: string | null = resume?.alternate_url || resume?.resumeUrl || null;
  const canShowPersonalData = resume?.pdnConsent === true || resume?.personalDataConsent === true;
  const contactPhones: string[] = canShowPersonalData && Array.isArray(resume?.contacts?.phones)
    ? resume.contacts.phones
      .map((phone: any) => (typeof phone?.value === 'string' ? phone.value.trim() : ''))
      .filter(Boolean)
    : [];
  const contactChatId: string | null = canShowPersonalData && typeof resume?.contacts?.chat?.value === 'string' && resume.contacts.chat.value.trim()
    ? resume.contacts.chat.value.trim()
    : null;
  const hasApplicationContacts = contactPhones.length > 0 || !!contactChatId;

  const relocationReadiness = formatReadiness(resume?.relocationReadiness);
  const businessTripReadiness = formatReadiness(resume?.businessTripReadiness);
  const driverLicence: boolean | null = typeof resume?.driverLicence === 'boolean'
    ? resume.driverLicence : null;
  const driverLicenceCategory: string[] = Array.isArray(resume?.driverLicenceCategory)
    ? resume.driverLicenceCategory : [];
  const drivingExperienceCode: string | null = typeof resume?.drivingExperience === 'string'
    ? resume.drivingExperience : null;
  const drivingExperienceText: string | null = drivingExperienceCode
    ? (DRIVING_EXPERIENCE_LABELS[drivingExperienceCode] || drivingExperienceCode)
    : null;
  const ownTransportCode: string | null = typeof resume?.ownTransport === 'string'
    ? resume.ownTransport : null;
  const ownTransportText: string | null = ownTransportCode
    ? (OWN_TRANSPORT_LABELS[ownTransportCode] || ownTransportCode)
    : null;
  const medicalBook: boolean | null = typeof resume?.medicalBook === 'boolean'
    ? resume.medicalBook : null;
  // Уровень образования: предпочитаем русский текст из params, fallback на
  // локализацию enum-кода (если только код пришёл).
  const educationLevelText: string | null = (typeof resume?.educationLevelText === 'string'
    && resume.educationLevelText.trim())
    ? resume.educationLevelText
    : (typeof resume?.educationLevelCode === 'string'
        ? (EDUCATION_LEVEL_LABELS[resume.educationLevelCode] || resume.educationLevelCode)
        : null);
  const specializationName: string | null = typeof resume?.specialization?.name === 'string'
    ? resume.specialization.name : null;
  const workPermitInRussia: string | null = typeof resume?.workPermitInRussia === 'string'
    ? resume.workPermitInRussia : null;
  const schedule: string | null = typeof resume?.schedule === 'string' ? resume.schedule : null;

  // Блок "Готовности" показываем если есть переезд/командировки/разрешение на работу
  const hasReadinessBlock =
    relocationReadiness != null ||
    businessTripReadiness != null ||
    workPermitInRussia != null;

  // Блок водительских прав теперь включает и стаж вождения, и свой транспорт —
  // они приходят на root уровне Resume2.0 (см. avitoMappers.js).
  const hasDriverBlock = driverLicence != null
    || driverLicenceCategory.length > 0
    || drivingExperienceText != null
    || ownTransportText != null;

  // Координаты — кликабельная ссылка на yandex maps, чтобы рекрутер мог
  // быстро посмотреть точку. Avito отдаёт latitude/longitude.
  const mapsUrl = coordinates
    ? `https://yandex.ru/maps/?ll=${coordinates.longitude},${coordinates.latitude}&z=15&pt=${coordinates.longitude},${coordinates.latitude}`
    : null;

  const hasLocationBlock = !!(cityName || fullAddress || metro || district || coordinates);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Шапка */}
        <div className="flex items-start justify-between p-6 border-b gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Фото"
                className="w-20 h-20 rounded-full object-cover flex-shrink-0 bg-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setIsImageZoomed(true)}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full flex-shrink-0 flex items-center justify-center text-2xl font-semibold"
                style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}
              >
                A
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 truncate">
                {position ? highlightText(position) : 'Резюме'}
              </h2>
              <p className="text-gray-600 mt-1 text-sm">
                <span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 mr-2 align-middle">
                  Avito
                </span>
                {idLabel}
              </p>
              {candidateName ? (
                <p className="text-gray-600 mt-1">
                  {candidateName}
                </p>
              ) : null}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Полноразмерный просмотр аватара. Закрытие — клик мимо или X. */}
        {isImageZoomed && photoUrl && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90 cursor-zoom-out"
            onClick={() => setIsImageZoomed(false)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]">
              <img
                src={photoUrl}
                alt="Фото (увеличено)"
                className="max-w-full max-h-[90vh] object-contain rounded-lg cursor-default"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                className="absolute top-[-40px] right-[-40px] text-white hover:text-gray-300 transition-colors"
                onClick={() => setIsImageZoomed(false)}
                aria-label="Закрыть"
              >
                <XMarkIcon className="h-10 w-10" />
              </button>
            </div>
          </div>
        )}

        {/* Содержимое */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Левая колонка */}
            <div>
              {/* Основная информация: показываем секцию только если есть хоть
                  одно поле. Каждое поле — самостоятельная строка с truthy-check. */}
              {(gender || resume?.age != null || salaryAmount != null
                || formattedExperience || citizenship || schedule
                || educationLevelText || specializationName) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Основная информация</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {salaryAmount != null && (
                      <p>
                        <span className="font-medium">Ожидаемая зарплата:</span>{' '}
                        {salaryAmount.toLocaleString('ru-RU')} ₽
                      </p>
                    )}
                    {gender && (
                      <p>
                        <span className="font-medium">Пол:</span>{' '}
                        {gender === 'male' ? 'Мужской' : gender === 'female' ? 'Женский' : gender}
                      </p>
                    )}
                    {resume?.age != null && (
                      <p><span className="font-medium">Возраст:</span> {resume.age}</p>
                    )}
                    {citizenship && (
                      <p><span className="font-medium">Гражданство:</span> {citizenship}</p>
                    )}
                    {formattedExperience && (
                      <p>
                        <span className="font-medium">Опыт:</span> {formattedExperience}
                      </p>
                    )}
                    {schedule && (
                      <p>
                        <span className="font-medium">График работы:</span> {schedule}
                      </p>
                    )}
                    {specializationName && (
                      <p>
                        <span className="font-medium">Специализация:</span> {specializationName}
                      </p>
                    )}
                    {educationLevelText && (
                      <p>
                        <span className="font-medium">Образование:</span> {educationLevelText}
                      </p>
                    )}
                    {resume?.updated_at && (
                      <p className="text-sm text-gray-500">
                        Обновлено: {formatDate(resume.updated_at)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Локация: полный адрес + метро + район + координаты. */}
              {hasLocationBlock && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Локация</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {cityName && (
                      <p>
                        <span className="font-medium">Город:</span>{' '}
                        <span className={isCityMatched(cityName) ? 'bg-green-200 px-1 rounded' : ''}>
                          {cityName}
                        </span>
                      </p>
                    )}
                    {fullAddress && (
                      <p><span className="font-medium">Адрес:</span> {fullAddress}</p>
                    )}
                    {metro && (
                      <p><span className="font-medium">Метро:</span> {metro}</p>
                    )}
                    {district && (
                      <p><span className="font-medium">Район:</span> {district}</p>
                    )}
                    {coordinates && mapsUrl && (
                      <p className="text-sm">
                        <span className="font-medium">Координаты:</span>{' '}
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 hover:underline"
                          style={{ color: colors.primary }}
                        >
                          {coordinates.latitude.toFixed(5)}, {coordinates.longitude.toFixed(5)}
                          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {canShowPersonalData && hasApplicationContacts ? (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Контакты</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  {contactPhones.map((phone) => (
                    <p key={phone} className="text-gray-700">
                      <span className="font-medium">Телефон:</span> {phone}
                    </p>
                  ))}
                  {contactChatId && (
                    <p className="text-gray-700">
                      <span className="font-medium">Чат Avito:</span> доступен
                    </p>
                  )}
                </div>
              </div>
              ) : null}

              {/* Языки */}
              {languages.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Языки</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                    {languages.map((lang, i) => (
                      <p key={i}>
                        <span className="font-medium">{lang.name}</span>
                        {lang.level ? `: ${lang.level}` : ''}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Готовности (переезд, командировки, разрешение на работу в РФ) */}
              {hasReadinessBlock && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Готовности</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {relocationReadiness && (
                      <p>
                        <span className="font-medium">Переезд:</span> {relocationReadiness}
                      </p>
                    )}
                    {businessTripReadiness && (
                      <p>
                        <span className="font-medium">Командировки:</span> {businessTripReadiness}
                      </p>
                    )}
                    {workPermitInRussia && (
                      <p>
                        <span className="font-medium">Разрешение на работу в РФ:</span>{' '}
                        {workPermitInRussia}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Водительские права. Все поля приходят на root-уровне Resume2.0
                  как машинные enum'ы, локализуем в UI. */}
              {hasDriverBlock && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Водительские права</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {driverLicence != null && (
                      <p>
                        <span className="font-medium">Права:</span>{' '}
                        {driverLicence ? 'Есть' : 'Нет'}
                      </p>
                    )}
                    {driverLicenceCategory.length > 0 && (
                      <p>
                        <span className="font-medium">Категории:</span>{' '}
                        {driverLicenceCategory.join(', ')}
                      </p>
                    )}
                    {drivingExperienceText && (
                      <p>
                        <span className="font-medium">Стаж вождения:</span>{' '}
                        {drivingExperienceText}
                      </p>
                    )}
                    {ownTransportText && (
                      <p>
                        <span className="font-medium">Свой транспорт:</span>{' '}
                        {ownTransportText}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Медкнижка — отдельный мини-блок, т.к. поле булево */}
              {medicalBook != null && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Медкнижка</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p>{medicalBook ? 'Есть' : 'Нет'}</p>
                  </div>
                </div>
              )}

              {/* Навыки */}
              {skills.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Навыки</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 rounded-full text-sm"
                          style={{
                            backgroundColor: `${colors.primary}20`,
                            color: colors.primary,
                            border: `1px solid ${colors.primary}40`,
                          }}
                        >
                          {highlightText(skill)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Образование */}
              {educations.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Образование</h3>
                  <div className="space-y-4">
                    {educations.map((edu, i) => (
                      <div key={i} className="bg-gray-50 p-4 rounded-lg">
                        {edu.name && (
                          <p className="font-semibold text-gray-900">{highlightText(edu.name)}</p>
                        )}
                        {edu.organization && (
                          <p className="text-gray-600">{highlightText(edu.organization)}</p>
                        )}
                        {edu.year && (
                          <p className="text-sm text-gray-500">Год окончания: {edu.year}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Правая колонка */}
            <div>
              {/* Опыт работы */}
              {experiences.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Опыт работы</h3>
                  <div className="space-y-4">
                    {experiences.map((exp, i) => (
                      <div key={i} className="bg-gray-50 p-4 rounded-lg">
                        {exp.position && (
                          <p className="font-semibold text-gray-900">{highlightText(exp.position)}</p>
                        )}
                        {exp.company && (
                          <p className="text-gray-600">{highlightText(exp.company)}</p>
                        )}
                        {(exp.start || exp.end) && (
                          <p className="text-sm text-gray-500">
                            {exp.start ? formatDate(exp.start) : ''} — {exp.end ? formatDate(exp.end) : 'по настоящее время'}
                          </p>
                        )}
                        {exp.description && (
                          <p className="mt-2 text-gray-700 whitespace-pre-wrap">
                            {highlightText(exp.description)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* "О себе" — у Avito ровно одно поле `description` (см. спеку
                  Resume2.0.description: "Описание резюме"). Plain-text без HTML,
                  рендерим whitespace-pre-wrap. Отдельного about_me / self_description
                  у Avito не существует, поэтому других источников нет. */}
              {description && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">О себе</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {highlightText(description)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Подвал */}
        <div className="p-6 border-t flex justify-between items-center gap-4">
          <div className="text-sm text-gray-500 max-w-2xl">
            {matchedKeywords.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="font-medium">Подсвечено:</span>
                {matchedKeywords.map((kw, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                    {kw}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            {alternateUrl && (
              <a
                href={alternateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-white rounded-3xl font-medium transition-colors inline-flex items-center gap-1"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = colors.primaryHover; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = colors.primary; }}
              >
                Открыть на Avito
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-white rounded-3xl font-medium transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvitoResumeModal;
